import React from "react";
import { Heart, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../context/CartContext";
import { useWishlist } from "../../../context/WishlistContext";
import "./SideCart.css";

const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-IN").format(Math.round(value || 0));

const SideCart: React.FC = () => {
    const navigate = useNavigate();
    const {
        cartItems,
        cartOpen,
        setCartOpen,
        removeItemFromCart,
        updateItemQuantity,
        cartTotal,
        cartCount,
    } = useCart();
    const { toggleWishlist } = useWishlist();

    const handleGoToCart = () => {
        setCartOpen(false);
        navigate("/Cart");
    };

    const handleContinueShopping = () => {
        setCartOpen(false);
    };

    const handleRemove = (itemId: string) => {
        if (!itemId) return;
        removeItemFromCart(itemId);
    };

    const handleIncrease = (itemId: string, currentQty: number) => {
        if (!itemId) return;
        updateItemQuantity(itemId, currentQty + 1);
    };

    const handleDecrease = (itemId: string, currentQty: number) => {
        if (!itemId) return;
        if (currentQty <= 1) {
            removeItemFromCart(itemId);
        } else {
            updateItemQuantity(itemId, currentQty - 1);
        }
    };

    const handleMoveToWishlist = (productId: string, itemId: string) => {
        if (productId && toggleWishlist) {
            toggleWishlist(productId);
        }
        if (itemId) {
            removeItemFromCart(itemId);
        }
    };

    React.useEffect(() => {
        if (cartOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [cartOpen]);

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && cartOpen) {
                setCartOpen(false);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [cartOpen, setCartOpen]);

    return (
        <>
            <div
                className={`side-cart-overlay ${cartOpen ? "open" : ""}`}
                onClick={() => setCartOpen(false)}
            />
            <aside className={`side-cart ${cartOpen ? "open" : ""}`}>
                <div className="side-cart-header">
                    <div>
                        <ShoppingBag size={19} strokeWidth={1.9} />
                        <h2>Your Cart</h2>
                        <span>
                            {cartCount} {cartCount === 1 ? "item" : "items"}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setCartOpen(false)}
                        aria-label="Close cart"
                    >
                        <X size={20} strokeWidth={1.8} />
                    </button>
                </div>

                {cartItems.length === 0 ? (
                    <div className="side-cart-empty">
                        <span>
                            <ShoppingBag size={25} strokeWidth={1.7} />
                        </span>
                        <h3>Your cart is empty</h3>
                        <p>Add products and they will appear here.</p>
                        <button type="button" onClick={handleContinueShopping}>
                            Continue Shopping
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="side-cart-items">
                            {cartItems.map((item, index) => {
                                const itemId = item.id || item.cartId || `cart-item-${index}`;
                                const title = item.productName || (item as any).title || "Product";
                                const image =
                                    item.productImage ||
                                    (item as any).image ||
                                    "/assets/images/products/groceries/grocery-1.png";
                                const description =
                                    item.metadata?.description || (item as any).description;
                                const color =
                                    item.metadata?.color || (item as any).color;
                                const size =
                                    item.metadata?.size || (item as any).size;
                                const variant =
                                    item.variantName || (item as any).variant;
                                const price =
                                    typeof item.finalPrice === "number" && item.finalPrice > 0
                                        ? item.finalPrice
                                        : item.unitPrice || (item as any).price || 0;
                                const mrp =
                                    item.metadata?.mrp || (item as any).mrp || 0;
                                const quantity = item.quantity || 1;

                                return (
                                    <article className="side-cart-item" key={itemId}>
                                        <div className="side-cart-item-image">
                                            <img
                                                src={image}
                                                alt={title}
                                                onError={(e) => {
                                                    e.currentTarget.src =
                                                        "/assets/images/products/groceries/grocery-1.png";
                                                }}
                                            />
                                        </div>
                                        <div className="side-cart-item-content">
                                            <div className="side-cart-item-top">
                                                <div className="side-cart-item-heading">
                                                    <h3>{title}</h3>
                                                    {description && (
                                                        <p className="side-cart-item-description">
                                                            {description}
                                                        </p>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="side-cart-remove"
                                                    onClick={() => handleRemove(itemId)}
                                                    aria-label={`Remove ${title}`}
                                                >
                                                    <Trash2 size={15} strokeWidth={1.9} />
                                                </button>
                                            </div>

                                            {(color || size || variant) && (
                                                <div className="side-cart-selection">
                                                    {color && (
                                                        <span>
                                                            Colour: <strong>{color}</strong>
                                                        </span>
                                                    )}
                                                    {size && (
                                                        <span>
                                                            Size: <strong>{size}</strong>
                                                        </span>
                                                    )}
                                                    {variant && (
                                                        <span>
                                                            Variant: <strong>{variant}</strong>
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            <div className="side-cart-price">
                                                <strong>₹ {formatPrice(price)}</strong>
                                                {mrp > price && (
                                                    <del>₹ {formatPrice(mrp)}</del>
                                                )}
                                            </div>

                                            <div className="side-cart-item-actions">
                                                <div className="side-cart-quantity">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleDecrease(itemId, quantity)
                                                        }
                                                        aria-label="Decrease quantity"
                                                    >
                                                        {quantity === 1 ? (
                                                            <Trash2 size={13} strokeWidth={2} />
                                                        ) : (
                                                            <Minus size={13} strokeWidth={2} />
                                                        )}
                                                    </button>
                                                    <span>{quantity}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleIncrease(itemId, quantity)
                                                        }
                                                        aria-label="Increase quantity"
                                                    >
                                                        <Plus size={13} strokeWidth={2} />
                                                    </button>
                                                </div>
                                                <span className="side-cart-item-total">
                                                    ₹ {formatPrice(price * quantity)}
                                                </span>
                                            </div>

                                            <div className="side-cart-secondary-actions">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleMoveToWishlist(
                                                            item.productId,
                                                            itemId
                                                        )
                                                    }
                                                >
                                                    <Heart size={13} strokeWidth={1.9} />
                                                    Move to wishlist
                                                </button>
                                                <span />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemove(itemId)}
                                                >
                                                    Save for later
                                                </button>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <div className="side-cart-footer">
                            <div className="side-cart-summary">
                                <span>
                                    Subtotal ({cartCount} {cartCount === 1 ? "item" : "items"})
                                </span>
                                <strong>₹ {formatPrice(cartTotal)}</strong>
                            </div>
                            <p>Shipping, taxes and offers are calculated at checkout.</p>
                            <button
                                type="button"
                                className="side-cart-checkout"
                                onClick={handleGoToCart}
                            >
                                Go to Cart
                            </button>
                        </div>
                    </>
                )}
            </aside>
        </>
    );
};

export default SideCart;